/**
 * E10 — Prompt identity helpers (lot E deploy 1).
 *
 * `languageKeyOf` and `promptIdentityKey` are the single source of truth for
 * the two inputs to the matrix join key.  They are also used by the duplicate
 * guard (deploy 2, E16) and `createZoneVariants` (deploy 2, E17).
 *
 * Never compute or re-normalize a zone key here — that is `storedPlaceKey`
 * from `zoneMeta.ts` (C1). This file only combines a textHash, a place key
 * and a language into one opaque identity string.
 */

/**
 * The DEFAULTED measurement language for a prompt.
 *
 * Returns the prompt's own language (lower-cased, trimmed) when set, else the
 * brand working language, else `'fr'`.  Matches the lot C step-11 resolution:
 *   `(row.language || '').trim().toLowerCase() || (workingLanguage || 'fr').toLowerCase()`
 *
 * Used to key the matrix row and the duplicate guard — NEVER to re-set the row's
 * `language` field.
 */
export function languageKeyOf(
  q: { language?: string | null },
  brand?: { strategy?: { workingLanguage?: string } } | null,
): string {
  return (
    (q.language ?? '').trim().toLowerCase() ||
    (brand?.strategy?.workingLanguage || 'fr').toLowerCase()
  );
}

/**
 * The opaque prompt identity key for the matrix and the duplicate guard.
 *
 * `place`    = C1 `storedPlaceKey(q)` (`'∅'` Mondial, `legacy:<zone>`, `p1:…`).
 * `language` = `languageKeyOf(q, brand)`.
 *
 * Format: `${textHash}|${place}|${language}`.
 *
 * Two prompts that differ in text, place or language produce distinct keys.
 * The `|` separator is safe: `storedPlaceKey` uses `|` internally only inside
 * `p1:…` keys (never at position 0), and U+001F (the row_hash separator) is
 * stripped from legacy zone keys by C1, so no injected `|` can fake a key.
 */
export function promptIdentityKey(
  textHash: string,
  place: string,
  language: string,
): string {
  return `${textHash}|${place}|${language}`;
}
